import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, Circle, Trophy } from 'lucide-react';
import { studentAPI } from '../utils/api';
import { formatDate } from '../utils/helpers';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const StudentTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await studentAPI.getTasks();
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const response = await studentAPI.completeTask(taskId);
      
      if (response.data.level_up) {
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.6 }
        });
        toast.success(`Você subiu para o nível ${response.data.new_level}!`);
        
        if (response.data.evolution_stage) {
          toast.success(`Seu monstro evoluiu para o estágio ${response.data.evolution_stage}!`, {
            duration: 5000
          });
        }
      } else {
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.7 }
        });
        toast.success(`+${response.data.xp_awarded} AURA!`);
      }
      
      loadTasks();
    } catch (error) {
      console.error('Failed to complete task:', error);
      if (error.response?.status === 400) {
        toast.error('Tarefa já concluída');
      } else {
        toast.error('Erro ao completar tarefa');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  const incompleteTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/student/dashboard')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2" data-testid="tasks-title">Suas Tarefas</h1>
          <p className="text-slate-600 font-nunito">Complete tarefas para ganhar AURA e evoluir seu monstro</p>
        </div>

        {/* Pending Tasks */}
        <div className="mb-12">
          <h2 className="text-xl font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Circle className="w-5 h-5 text-secondary" />
            Tarefas Pendentes ({incompleteTasks.length})
          </h2>
          
          {incompleteTasks.length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-12 text-center">
              <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-nunito">Nenhuma tarefa pendente no momento</p>
            </div>
          ) : (
            <div className="space-y-4">
              {incompleteTasks.map((task) => (
                <div
                  key={task.task_id}
                  data-testid="pending-task-card"
                  className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm hover:border-blue-200 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-outfit font-bold text-slate-900">{task.title}</h3>
                        {task.classroom_name && (
                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-lg text-xs font-bold">
                            {task.classroom_name}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 font-nunito mb-4">{task.description}</p>
                      {task.due_date && (
                        <p className="text-sm text-slate-500 font-nunito">
                          Prazo: {formatDate(task.due_date)}
                        </p>
                      )}
                    </div>
                    <div className="ml-6 flex flex-col items-end gap-3">
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                        +{task.xp_reward} AURA
                      </div>
                      <Button
                        data-testid="complete-task-btn"
                        onClick={() => handleCompleteTask(task.task_id)}
                        className="bg-accent hover:bg-accent/90 text-white h-10 px-6 rounded-xl font-bold transition-all"
                      >
                        Concluir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Completed Tasks */}
        {completedTasks.length > 0 && (
          <div>
            <h2 className="text-xl font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-accent" />
              Tarefas Concluídas ({completedTasks.length})
            </h2>
            <div className="space-y-4">
              {completedTasks.map((task) => (
                <div
                  key={task.task_id}
                  data-testid="completed-task-card"
                  className="bg-white rounded-2xl border-2 border-green-100 p-6 shadow-sm opacity-75"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <CheckCircle2 className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-outfit font-bold text-slate-900">{task.title}</h3>
                          {task.classroom_name && (
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-lg text-xs font-bold">
                              {task.classroom_name}
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 font-nunito mb-2">{task.description}</p>
                        {task.completion && (
                          <p className="text-sm text-green-600 font-nunito font-bold">
                            Concluída em {formatDate(task.completion.completed_at)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="bg-green-100 text-green-700 px-4 py-2 rounded-xl font-bold ml-6">
                      +{task.xp_reward} AURA
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentTasks;
